import { useMutation } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { KeyRound, Mail } from "lucide-react"
import { type SetStateAction, useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { authClient } from "../lib/auth/authClient"

export const Route = createFileRoute("/forgotPassword")({
  component: ForgotPassword,
})

export default function ForgotPassword() {
  const [email, setEmail] = useState("")
  const [isSubmitted, setIsSubmitted] = useState(false)

  const { mutate: resetPassword, isPending: isSubmitting } = useMutation({
    mutationFn: async (email: string) => {
      // @ts-expect-error — emailOTPClient types forgetPassword as a namespace but the base fn is still callable
      const result = await authClient.forgetPassword({
        email,
        redirectTo: `${window.location.origin}/resetPassword`,
      })
      if (result.error) {
        throw new Error(result.error.message ?? "Failed to send reset email")
      }
    },
    onSuccess: () => {
      toast.success("Password reset instructions sent to your email")
      setIsSubmitted(true)
    },
    onError: (error) => {
      toast.error("Something went wrong. Please try again.")
      console.error("Password reset error:", error)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!email) {
      toast.error("Please enter your email address")
      return
    }

    resetPassword(email)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <div className="flex justify-center">
            <KeyRound size={36} className="text-primary" />
          </div>
          <CardTitle className="text-2xl">Forgot Password</CardTitle>
          <CardDescription>
            {!isSubmitted
              ? "Enter your email and we'll send you password reset instructions"
              : "Check your email for reset instructions"}
          </CardDescription>
        </CardHeader>

        {!isSubmitted ? (
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute top-3 left-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    className="pl-10"
                    value={email}
                    onChange={(e: { target: { value: SetStateAction<string> } }) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex-col space-y-4">
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send Reset Instructions"}
              </Button>
              <div className="text-center text-muted-foreground text-sm">
                Remember your password?{" "}
                <Link to="/login" className="text-primary hover:underline">
                  Back to login
                </Link>
              </div>
            </CardFooter>
          </form>
        ) : (
          <CardContent className="space-y-4">
            <div className="rounded-lg bg-muted p-4 text-center">
              <p>We've sent reset instructions to:</p>
              <p className="mt-1 font-medium">{email}</p>
            </div>
            <div className="flex flex-col space-y-2">
              <Button asChild variant="outline">
                <Link to="/login">Back to login</Link>
              </Button>
              <Button variant="link" onClick={() => setIsSubmitted(false)}>
                Didn't receive the email? Try again
              </Button>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  )
}
